import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Alert,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Spinner,
} from 'reactstrap';
import { ApiContext } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';
import WhatsappService from '../../service/whatsapp.service';
import BotService from '../../service/bot.service';
import WhatsappStatusBar from '../../components/Whatsapp/WhatsappStatusBar';
import { useWhatsappSession, getWhatsappStatusLabel } from '../../hooks/useWhatsappSession';

const botCardStyle = {
  height: '100%',
  border: '1px solid rgba(255,255,255,0.08)',
};

const botHintStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  padding: '12px 14px',
  fontSize: 13,
  color: '#9a9a9a',
  lineHeight: 1.55,
};

const copyFieldStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 6,
  padding: '6px 6px 6px 10px',
};

const QUALITY = {
  GREEN: { label: 'Qualidade alta', color: 'success' },
  YELLOW: { label: 'Qualidade média', color: 'warning' },
  RED: { label: 'Qualidade baixa', color: 'danger' },
};

const normalText = { textTransform: 'none', letterSpacing: 'normal' };

const emptyCloudForm = { wabaId: '', phoneNumberId: '', accessToken: '', appSecret: '' };

const errorMessage = (err, fallback) => {
  const msg = err?.response?.data?.message;
  return Array.isArray(msg) ? msg.join(', ') : msg || fallback;
};

function WhatsappConnectionPage() {
  const { account, isSuperAdmin } = useContext(ApiContext);
  const { notify } = useNotification();
  const accountId = account?.id;
  const provider = account?.whatsappProvider || 'baileys';
  const isWaba = provider === 'waba';
  const { session, loading, refresh } = useWhatsappSession(accountId);
  const [actionLoading, setActionLoading] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Mensagem de teste do sistema PedeGo.');

  const [cloud, setCloud] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [editingCloud, setEditingCloud] = useState(false);
  const [cloudForm, setCloudForm] = useState(emptyCloudForm);

  const [botLoading, setBotLoading] = useState(true);
  const [botSaving, setBotSaving] = useState(false);
  const [botEnabled, setBotEnabled] = useState(false);
  const [typebotPublicId, setTypebotPublicId] = useState('');

  const loadBot = async () => {
    if (!accountId) return;
    setBotLoading(true);
    try {
      const data = await BotService.getConfig(accountId);
      setBotEnabled(!!data?.enabled);
      setTypebotPublicId(data?.typebotPublicId || '');
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar o bot', 'icon-alert-circle-exc');
    } finally {
      setBotLoading(false);
    }
  };

  const loadCloud = async () => {
    if (!accountId || !isWaba) return;
    setCloudLoading(true);
    try {
      setCloud(await WhatsappService.getCloudConfig(accountId));
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', errorMessage(err, 'Erro ao carregar a integração'), 'icon-alert-circle-exc');
    } finally {
      setCloudLoading(false);
    }
  };

  useEffect(() => {
    loadBot();
  }, [accountId]);

  useEffect(() => {
    setEditingCloud(false);
    loadCloud();
  }, [accountId, isWaba]);

  const runAction = async (fn, successMsg, reloadCloud = false) => {
    setActionLoading(true);
    try {
      await fn();
      await refresh();
      if (successMsg) notify('tr', 'success', successMsg, 'icon-check-2');
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', errorMessage(err, 'Erro na operação do WhatsApp'), 'icon-alert-circle-exc');
    } finally {
      setActionLoading(false);
      if (reloadCloud) loadCloud();
    }
  };

  const openCloudForm = () => {
    setCloudForm({
      ...emptyCloudForm,
      wabaId: cloud?.wabaId || '',
      phoneNumberId: cloud?.phoneNumberId || '',
    });
    setEditingCloud(true);
  };

  const handleSaveCloud = async (e) => {
    e.preventDefault();
    const payload = {
      wabaId: cloudForm.wabaId.trim(),
      phoneNumberId: cloudForm.phoneNumberId.trim(),
      ...(cloudForm.accessToken.trim() ? { accessToken: cloudForm.accessToken.trim() } : {}),
      ...(cloudForm.appSecret.trim() ? { appSecret: cloudForm.appSecret.trim() } : {}),
    };
    setActionLoading(true);
    try {
      await WhatsappService.saveCloudConfig(accountId, payload);
      setEditingCloud(false);
      notify('tr', 'success', 'WhatsApp Cloud API conectado', 'icon-check-2');
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', errorMessage(err, 'Não foi possível conectar à Meta'), 'icon-alert-circle-exc');
    } finally {
      setActionLoading(false);
      loadCloud();
      refresh();
    }
  };

  const handleCloudLogout = () => {
    if (
      !window.confirm(
        'Desconectar remove o webhook da Meta. Notificações de pedido e o Miranda Bot param até sincronizar de novo. Continuar?',
      )
    ) {
      return;
    }
    runAction(() => WhatsappService.logout(accountId), 'WhatsApp desconectado', true);
  };

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      notify('tr', 'success', `${label} copiado`, 'icon-check-2');
    } catch {
      notify('tr', 'warning', 'Não foi possível copiar. Selecione e copie manualmente.', 'icon-alert-circle-exc');
    }
  };

  const saveBotConfig = async (payload, successMsg) => {
    setBotSaving(true);
    try {
      const data = await BotService.saveConfig(accountId, payload);
      setBotEnabled(!!data?.enabled);
      setTypebotPublicId(data?.typebotPublicId || '');
      notify('tr', 'success', successMsg, 'icon-check-2');
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao salvar o bot',
        'icon-alert-circle-exc',
      );
    } finally {
      setBotSaving(false);
    }
  };

  const handleSaveTypebot = (e) => {
    e.preventDefault();
    const publicId = typebotPublicId.trim();
    if (!publicId) {
      notify('tr', 'warning', 'Informe o ID público do Miranda Bot', 'icon-alert-circle-exc');
      return;
    }
    saveBotConfig(
      isSuperAdmin ? { enabled: true, typebotPublicId: publicId } : { typebotPublicId: publicId },
      'Configuração do bot salva',
    );
  };

  const handleToggleBot = (enabled) => {
    saveBotConfig({ enabled }, enabled ? 'Miranda Bot habilitado nesta conta' : 'Miranda Bot desabilitado nesta conta');
  };

  const status = session?.status || 'DISCONNECTED';
  const qr = session?.qr;
  const waConnected = status === 'CONNECTED';
  const botReady = botEnabled && !!typebotPublicId.trim();

  const testForm = (
    <Card className="card-plain mt-3">
      <CardBody className="px-0 pb-0">
        <h5 className="mb-3">Teste de envio</h5>
        {isWaba && (
          <div style={botHintStyle} className="mb-3">
            Na Cloud API, texto livre só é entregue para quem mandou mensagem para este número nas últimas 24h.
            Fora dessa janela a Meta recusa o envio. Mande um &quot;oi&quot; do número de teste antes.
          </div>
        )}
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            runAction(
              () => WhatsappService.sendTest(accountId, testPhone, testMessage),
              'Mensagem de teste enviada',
            );
          }}
        >
          <FormGroup>
            <Label for="testPhone">Número (com DDD)</Label>
            <Input
              id="testPhone"
              placeholder="5511999999999"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label for="testMessage">Mensagem</Label>
            <Input
              id="testMessage"
              type="textarea"
              rows={3}
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              required
            />
          </FormGroup>
          <Button color="success" type="submit" disabled={actionLoading || loading}>
            Enviar teste
          </Button>
        </Form>
      </CardBody>
    </Card>
  );

  const copyField = (label, value) => (
    <FormGroup className="mb-2">
      <Label className="mb-1">{label}</Label>
      <div style={copyFieldStyle}>
        <code style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#ddd' }}>
          {value || '—'}
        </code>
        <Button size="sm" color="link" className="m-0 py-1" disabled={!value} onClick={() => copy(value, label)}>
          Copiar
        </Button>
      </div>
    </FormGroup>
  );

  const phoneInfo = cloud?.phone;
  const quality = QUALITY[phoneInfo?.qualityRating];

  const wabaCard = (
    <Card style={botCardStyle}>
      <CardHeader className="d-flex align-items-start justify-content-between" style={{ gap: 12 }}>
        <div>
          <CardTitle tag="h4" className="mb-1">Conexão WhatsApp oficial (Meta)</CardTitle>
          <p className="card-category mb-0" style={normalText}>
            Conecte o WhatsApp oficial da loja para avisar o cliente sobre o pedido e atender pelo Miranda Bot.
          </p>
        </div>
        <Badge color={waConnected ? 'success' : 'danger'} pill>
          {getWhatsappStatusLabel(status)}
        </Badge>
      </CardHeader>
      <CardBody>
        {cloudLoading && !cloud ? (
          <div className="text-center py-4">
            <Spinner size="sm" />
          </div>
        ) : editingCloud ? (
          <Form onSubmit={handleSaveCloud} autoComplete="off">
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="wabaId">ID da conta do WhatsApp Business</Label>
                  <Input
                    id="wabaId"
                    inputMode="numeric"
                    pattern="\d+"
                    title="Somente números"
                    placeholder="ex: 102290129340398"
                    value={cloudForm.wabaId}
                    onChange={(e) => setCloudForm({ ...cloudForm, wabaId: e.target.value.replace(/\D/g, '') })}
                    required
                  />
                  <small className="text-muted d-block mt-1">Na Meta aparece como &quot;WABA ID&quot;.</small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="phoneNumberId">ID do número de telefone</Label>
                  <Input
                    id="phoneNumberId"
                    inputMode="numeric"
                    pattern="\d+"
                    title="Somente números"
                    placeholder="ex: 106540352242922"
                    value={cloudForm.phoneNumberId}
                    onChange={(e) => setCloudForm({ ...cloudForm, phoneNumberId: e.target.value.replace(/\D/g, '') })}
                    required
                  />
                  <small className="text-muted d-block mt-1">
                    Na Meta aparece como &quot;Phone Number ID&quot;. Não é o número do WhatsApp.
                  </small>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label for="accessToken">Token de acesso permanente</Label>
                  <Input
                    id="accessToken"
                    type="password"
                    autoComplete="new-password"
                    placeholder={cloud?.hasAccessToken ? '•••••••• salvo — deixe em branco para manter' : 'EAAG...'}
                    value={cloudForm.accessToken}
                    onChange={(e) => setCloudForm({ ...cloudForm, accessToken: e.target.value })}
                    required={!cloud?.hasAccessToken}
                  />
                  <small className="text-muted d-block mt-1">
                    Gerado em Configurações do negócio → Usuários do sistema. O token temporário da tela de teste expira em 24h.
                  </small>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label for="appSecret">
                    Chave secreta do app <span className="text-muted">(opcional, recomendado)</span>
                  </Label>
                  <Input
                    id="appSecret"
                    type="password"
                    autoComplete="new-password"
                    placeholder={cloud?.hasAppSecret ? '•••••••• salvo — deixe em branco para manter' : 'Chave secreta do app'}
                    value={cloudForm.appSecret}
                    onChange={(e) => setCloudForm({ ...cloudForm, appSecret: e.target.value })}
                  />
                  <small className="text-muted d-block mt-1">
                    Na Meta aparece como &quot;App Secret&quot;, em Configurações do app → Básico. Com ela o sistema
                    confirma que cada aviso recebido veio mesmo da Meta.
                  </small>
                </FormGroup>
              </Col>
            </Row>
            <div style={botHintStyle} className="mb-3">
              Os dois IDs ficam no painel do app na Meta:{' '}
              <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer">
                developers.facebook.com
              </a>{' '}
              → seu app → WhatsApp → Configuração da API. Os dados ficam guardados criptografados.
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              <Button color="primary" type="submit" disabled={actionLoading}>
                {actionLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Validando na Meta...
                  </>
                ) : (
                  'Salvar e conectar'
                )}
              </Button>
              <Button color="secondary" outline type="button" disabled={actionLoading} onClick={() => setEditingCloud(false)}>
                Cancelar
              </Button>
            </div>
          </Form>
        ) : !cloud?.configured ? (
          <>
            <Alert color="warning">
              WhatsApp não conectado. Clique em Conectar e informe os dados da integração da Meta.
            </Alert>
            <Button color="primary" onClick={openCloudForm}>
              Conectar
            </Button>
          </>
        ) : (
          <>
            {cloud.lastError && (
              <Alert color="danger">
                <strong>Falha na última sincronização:</strong> {cloud.lastError}
              </Alert>
            )}
            {waConnected && !cloud.lastError && (
              <Alert color="success">
                Conectado à Meta. Mensagens recebidas chegam pelo webhook desta conta.
              </Alert>
            )}
            {!cloud.hasAppSecret && (
              <Alert color="warning">
                Sem a chave secreta do app, o sistema não consegue confirmar que os avisos recebidos vieram da Meta.
                Adicione em <strong>Editar dados</strong> quando tiver acesso a ela.
              </Alert>
            )}

            <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
              <strong>{phoneInfo?.verifiedName || 'Número'}</strong>
              <span className="text-muted">
                {phoneInfo?.displayPhoneNumber || session?.phoneNumber || cloud.phoneNumber || '—'}
              </span>
              {quality && (
                <Badge color={quality.color} pill>
                  {quality.label}
                </Badge>
              )}
              {phoneInfo && (
                <Badge color={phoneInfo.isOnBizApp ? 'info' : 'secondary'} pill>
                  {phoneInfo.isOnBizApp ? 'Coexistência · WhatsApp Business app' : 'Somente API'}
                </Badge>
              )}
            </div>
            {phoneInfo?.isOnBizApp && (
              <div style={botHintStyle} className="mb-3">
                Número em coexistência: o app WhatsApp Business continua funcionando no celular. Mensagens
                enviadas pelo app não passam por aqui e não são cobradas; o bot só responde o que chega pela API.
              </div>
            )}

            {copyField('URL do webhook', cloud.webhookUrl)}
            {copyField('Token de verificação', cloud.verifyToken)}
            <small className="text-muted d-block mb-3">
              Configurado automaticamente na Meta ao salvar ou sincronizar.
              {cloud.syncedAt ? ` Última sincronização: ${new Date(cloud.syncedAt).toLocaleString('pt-BR')}.` : ''}
            </small>

            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              <Button
                color="primary"
                disabled={actionLoading}
                onClick={() => runAction(() => WhatsappService.syncCloud(accountId), 'Sincronizado com a Meta', true)}
              >
                {actionLoading ? 'Sincronizando...' : 'Sincronizar'}
              </Button>
              <Button color="info" outline disabled={actionLoading} onClick={openCloudForm}>
                Editar dados
              </Button>
              {waConnected && (
                <Button color="danger" outline disabled={actionLoading} onClick={handleCloudLogout}>
                  Desconectar
                </Button>
              )}
            </div>

            {waConnected && testForm}
          </>
        )}
      </CardBody>
    </Card>
  );

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <WhatsappStatusBar />
        </Col>
      </Row>
      <Row>
        <Col lg="7" className="mb-4">
          {isWaba ? wabaCard : (
          <Card style={botCardStyle}>
            <CardHeader>
              <CardTitle tag="h4">Conexão WhatsApp</CardTitle>
              <p className="card-category mb-0">
                Escaneie o QR Code com o WhatsApp do estabelecimento para enviar notificações e atender pelo bot.
              </p>
            </CardHeader>
            <CardBody>
              <p>
                Status: <strong>{getWhatsappStatusLabel(status)}</strong>
                {session?.phoneNumber ? ` · ${session.phoneNumber}` : ''}
              </p>

              {(status === 'QR' || status === 'CONNECTING') && qr && (
                <div className="text-center mb-4">
                  <img src={qr} alt="QR Code WhatsApp" style={{ maxWidth: 280, borderRadius: 8 }} />
                  <p className="text-muted mt-2">Abra o WhatsApp → Aparelhos conectados → Conectar aparelho</p>
                </div>
              )}

              {status === 'DISCONNECTED' && (
                <Alert color="warning">
                  WhatsApp desconectado. Inicie a sessão para gerar um novo QR Code.
                </Alert>
              )}

              {status === 'CONNECTED' && (
                <Alert color="success">
                  WhatsApp conectado. As mensagens de status do pedido serão enviadas automaticamente.
                </Alert>
              )}

              {status === 'CONNECTED' && testForm}

              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                {(status === 'DISCONNECTED' || !session) && (
                  <Button
                    color="primary"
                    disabled={actionLoading || loading}
                    onClick={() => runAction(() => WhatsappService.start(accountId), 'Sessão iniciada')}
                  >
                    Conectar
                  </Button>
                )}
                {status !== 'DISCONNECTED' && (
                  <Button
                    color="info"
                    outline
                    disabled={actionLoading || loading}
                    onClick={() => runAction(() => WhatsappService.restart(accountId), 'Sessão reiniciada')}
                  >
                    Reiniciar sessão
                  </Button>
                )}
                {status !== 'DISCONNECTED' && (
                  <Button
                    color="danger"
                    outline
                    disabled={actionLoading || loading}
                    onClick={() => runAction(() => WhatsappService.logout(accountId), 'Sessão encerrada')}
                  >
                    Desconectar
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
          )}
        </Col>

        <Col lg="5" className="mb-4">
          <Card style={botCardStyle}>
            <CardHeader className="d-flex align-items-start justify-content-between" style={{ gap: 12 }}>
              <div>
                <CardTitle tag="h4" className="mb-1">Atendimento automático</CardTitle>
                <p className="card-category mb-0">
                  Responde o cliente no WhatsApp com o fluxo do Miranda Bot.
                </p>
              </div>
              <Badge color={botEnabled ? 'success' : 'secondary'} pill>
                {botEnabled ? 'Ativo' : 'Desligado'}
              </Badge>
            </CardHeader>
            <CardBody>
              {botLoading ? (
                <div className="text-center py-4">
                  <Spinner size="sm" />
                </div>
              ) : !botEnabled ? (
                <div>
                  <div
                    className="d-flex align-items-start"
                    style={{
                      gap: 12,
                      padding: '16px 14px',
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <i className="tim-icons icon-key-25" style={{ fontSize: 22, marginTop: 2, color: '#8b8b8b' }} />
                    <div>
                      <strong style={{ display: 'block', marginBottom: 4 }}>Miranda Bot não habilitado</strong>
                      <span className="text-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                        O atendimento automático precisa ser liberado pelo suporte nesta conta.
                      </span>
                    </div>
                  </div>
                  {isSuperAdmin && (
                    <Button
                      color="primary"
                      className="mt-3"
                      disabled={botSaving}
                      onClick={() => handleToggleBot(true)}
                    >
                      {botSaving ? 'Habilitando...' : 'Habilitar Miranda Bot nesta conta'}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {botReady && waConnected && (
                    <Alert color="success" className="mb-3">
                      Miranda Bot pronto. Mensagens recebidas neste WhatsApp seguem o fluxo configurado.
                    </Alert>
                  )}
                  {botReady && !waConnected && (
                    <Alert color="warning" className="mb-3">
                      O Miranda Bot está configurado, mas o WhatsApp precisa estar conectado para responder.
                    </Alert>
                  )}
                  {!botReady && (
                    <Alert color="info" className="mb-3">
                      Informe o ID público do Miranda Bot para começar o atendimento.
                    </Alert>
                  )}

                  <Form onSubmit={handleSaveTypebot}>
                    <FormGroup>
                      <Label for="typebotPublicId">ID público do Miranda Bot</Label>
                      <Input
                        id="typebotPublicId"
                        value={typebotPublicId}
                        onChange={(e) => setTypebotPublicId(e.target.value)}
                        placeholder="ex: atendimento-pizzaria"
                        autoComplete="off"
                      />
                    </FormGroup>
                    <div style={botHintStyle} className="mb-3">
                      No Miranda Bot: compartilhe o bot e copie o <strong style={{ color: '#ddd' }}>public ID</strong> da URL
                      (ou em Configurações → Compartilhar).
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                      <Button color="primary" type="submit" disabled={botSaving}>
                        {botSaving ? 'Salvando...' : 'Salvar configuração'}
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          color="danger"
                          outline
                          type="button"
                          disabled={botSaving}
                          onClick={() => handleToggleBot(false)}
                        >
                          Desabilitar
                        </Button>
                      )}
                    </div>
                  </Form>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default WhatsappConnectionPage;
